/**
 * Nutrition Foods API Contract
 *
 * oRPC contract for nutrition-foods endpoints.
 * Domain types come from @animus/habit-modules.
 */

import { oc } from "@orpc/contract";
import { z } from "zod";

import type { NutritionFood, SeedFood } from "@animus/habit-modules/nutrition";

// Re-export domain types for convenience
export type {
  FoodGroupCode,
  NutritionFood,
  NutritionModifierSlug,
  NutritionTag,
  SeedFood,
  SeedFoodSource,
} from "@animus/habit-modules/nutrition";

// ============================================
// Zod Schemas for API Validation
// ============================================

export const DataSourceSchema = z.enum([
  "usda_fdc",
  "usda_sr",
  "open_food_facts",
  "ai_estimated",
  "seed",
]);

export const EnrichmentStatusSchema = z.enum([
  "pending",
  "enriched",
  "verified",
]);

export const SeedFoodSourceSchema = z.object({
  type: DataSourceSchema,
  confidence: z.number().min(0).max(1),
});

export const SeedFoodSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  normalizedName: z.string().min(1).max(200),
  description: z.string().nullable(),
  calories: z.number().nonnegative(),
  protein: z.number().nonnegative(),
  fat: z.number().nonnegative(),
  carbs: z.number().nonnegative(),
  fiber: z.number().nonnegative(),
  saturatedFat: z.number().nonnegative(),
  foodGroup: z.string().min(1).max(10),
  tags: z.array(z.string()),
  compatibleModifiers: z.array(z.string()),
  enrichmentStatus: EnrichmentStatusSchema,
  source: SeedFoodSourceSchema,
});

// ============================================
// Request/Response Types
// ============================================

export interface ListFoodsRequest {
  limit?: number;
  offset?: number;
  search?: string;
  foodGroup?: string;
  tags?: string[];
}

export interface ListFoodsResponse {
  foods: NutritionFood[];
  total: number;
  limit: number;
  offset: number;
}

export interface ImportFoodsRequest {
  foods: SeedFood[];
}

export interface ImportFoodsResponse {
  imported: number;
  updated: number;
  errors: string[];
}

export interface NutritionFoodsApiError {
  code: string;
  message: string;
}

// ============================================
// oRPC Contract Schemas
// ============================================

export const ListFoodsRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
  search: z.string().optional(),
  foodGroup: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export const ListFoodsResponseSchema = z.object({
  foods: z.array(z.any()), // Output validation uses domain type
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

export const ImportFoodsRequestSchema = z.object({
  foods: z.array(SeedFoodSchema),
});

export const ImportFoodsResponseSchema = z.object({
  imported: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  errors: z.array(z.string()),
});

// ============================================
// oRPC Contract Definition
// ============================================

/**
 * Nutrition Foods oRPC contract.
 */
export const nutritionFoodsContract = {
  /**
   * List nutrition foods with optional search and filtering.
   */
  list: oc
    .route({ method: "GET", path: "/nutrition/foods" })
    .input(ListFoodsRequestSchema)
    .output(ListFoodsResponseSchema),

  /**
   * Bulk import nutrition foods via upsert.
   */
  import: oc
    .route({ method: "POST", path: "/nutrition/foods/import" })
    .input(ImportFoodsRequestSchema)
    .output(ImportFoodsResponseSchema),
};
